// Password validation utilities for AWS Cognito password policy compliance

export interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
  strength: 'weak' | 'medium' | 'strong';
}

export interface PasswordRequirement {
  label: string;
  met: boolean;
  regex?: RegExp;
}

/**
 * Validates password against AWS Cognito default password policy
 * Requirements:
 * - Minimum 8 characters
 * - At least 1 uppercase letter
 * - At least 1 lowercase letter
 * - At least 1 number
 * - At least 1 special character
 */
export const validatePassword = (password: string): PasswordValidationResult => {
  const errors: string[] = [];

  // Check minimum length
  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long');
  }

  // Check for uppercase letter
  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }

  // Check for lowercase letter
  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }

  // Check for number
  if (!/[0-9]/.test(password)) {
    errors.push('Password must contain at least one number');
  }

  // Check for special character
  if (!/[^A-Za-z0-9]/.test(password)) {
    errors.push('Password must contain at least one special character (!@#$%^&*(),.?":{}|<>)');
  }

  // Calculate password strength
  const strength = calculatePasswordStrength(password);

  return {
    isValid: errors.length === 0,
    errors,
    strength,
  };
};

/**
 * Gets detailed password requirements with their met status
 */
export const getPasswordRequirements = (password: string): PasswordRequirement[] => {
  return [
    {
      label: 'At least 8 characters',
      met: password.length >= 8,
    },
    {
      label: 'One uppercase letter (A-Z)',
      met: /[A-Z]/.test(password),
    },
    {
      label: 'One lowercase letter (a-z)',
      met: /[a-z]/.test(password),
    },
    {
      label: 'One number (0-9)',
      met: /[0-9]/.test(password),
    },
    {
      label: 'One special character (!@#$%...)',
      met: /[^A-Za-z0-9]/.test(password),
    },
  ];
};

/**
 * Calculates password strength based on multiple criteria
 */
export const calculatePasswordStrength = (password: string): 'weak' | 'medium' | 'strong' => {
  let score = 0;

  // Length scoring
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  if (password.length >= 16) score += 1;

  // Character variety scoring
  if (/[A-Z]/.test(password)) score += 1;
  if (/[a-z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;

  // Complexity bonuses
  const hasMultipleUppercase = (password.match(/[A-Z]/g) || []).length >= 2;
  const hasMultipleNumbers = (password.match(/[0-9]/g) || []).length >= 2;
  const hasMultipleSpecial = (password.match(/[^A-Za-z0-9]/g) || []).length >= 2;

  if (hasMultipleUppercase) score += 0.5;
  if (hasMultipleNumbers) score += 0.5;
  if (hasMultipleSpecial) score += 0.5;

  // Determine strength category
  if (score < 5) return 'weak';
  if (score < 7) return 'medium';
  return 'strong';
};

/**
 * Validates that two passwords match
 */
export const validatePasswordsMatch = (password: string, confirmPassword: string): boolean => {
  return password === confirmPassword && password.length > 0;
};

/**
 * Gets user-friendly error message for Cognito errors
 */
export const getCognitoErrorMessage = (error: any): string => {
  const errorName = error?.name || '';
  const errorCode = error?.code || '';

  // Handle specific Cognito error types
  if (errorName === 'InvalidPasswordException' || errorCode === 'InvalidPasswordException') {
    return 'Password does not meet requirements. Must contain uppercase, lowercase, number, and special character.';
  }

  if (errorName === 'NotAuthorizedException' || errorCode === 'NotAuthorizedException') {
    return 'Current password is incorrect. Please try again.';
  }

  if (errorName === 'LimitExceededException' || errorCode === 'LimitExceededException') {
    return 'Too many failed attempts. Please try again later.';
  }

  if (errorName === 'UserNotFoundException' || errorCode === 'UserNotFoundException') {
    return 'User account not found. Please check your email address.';
  }

  if (errorName === 'CodeMismatchException' || errorCode === 'CodeMismatchException') {
    return 'Invalid verification code. Please check and try again.';
  }

  if (errorName === 'ExpiredCodeException' || errorCode === 'ExpiredCodeException') {
    return 'Verification code has expired. Please request a new code.';
  }

  if (errorName === 'InvalidParameterException' || errorCode === 'InvalidParameterException') {
    return 'Invalid input. Please check your information and try again.';
  }

  // Network and generic errors
  if (errorName === 'NetworkError' || error?.message?.includes('network')) {
    return 'Network error. Please check your connection and try again.';
  }

  // Default fallback
  return error?.message || 'An error occurred. Please try again.';
};
